import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const SCROLL_LOCK_SELECTORS = [
    document.documentElement,
    document.body,
    () => document.querySelector('.student-main'),
    () => document.querySelector('.student-container'),
    () => document.querySelector('.lesson-modal-panel'),
    () => document.querySelector('.modal-overlay'),
];

function lockPageScroll() {
    const locked = SCROLL_LOCK_SELECTORS
        .map((sel) => (typeof sel === 'function' ? sel() : sel))
        .filter(Boolean);
    const prev = locked.map((el) => ({ el, overflow: el.style.overflow }));
    locked.forEach((el) => { el.style.overflow = 'hidden'; });
    return () => {
        prev.forEach(({ el, overflow }) => { el.style.overflow = overflow; });
    };
}

/** Ignore the same tap that opened the lightbox (mobile ghost click closes it instantly). */
export function useImageLightbox() {
    const [expandedSrc, setExpandedSrc] = useState(null);
    const ignoreCloseUntilRef = useRef(0);

    const openImage = useCallback((src) => {
        if (!src) return;
        ignoreCloseUntilRef.current = Date.now() + 450;
        setExpandedSrc(src);
    }, []);

    const closeImage = useCallback(() => {
        if (Date.now() < ignoreCloseUntilRef.current) return;
        setExpandedSrc(null);
    }, []);

    return { expandedSrc, openImage, closeImage };
}

export function ImageLightboxPortal({ src, onClose, ariaLabel = 'Expanded image' }) {
    useEffect(() => {
        if (!src) return undefined;
        const unlock = lockPageScroll();
        const onKey = (e) => {
            if (e.key === 'Escape') onClose?.();
        };
        window.addEventListener('keydown', onKey);
        return () => {
            unlock();
            window.removeEventListener('keydown', onKey);
        };
    }, [src, onClose]);

    if (!src) return null;

    return createPortal(
        <div
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
            className="expandable-image-lightbox"
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                height: '100%',
                zIndex: 2147483646,
                background: 'rgba(15, 23, 42, 0.95)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '12px',
                boxSizing: 'border-box',
                cursor: 'zoom-out',
                overflow: 'hidden',
                overscrollBehavior: 'none',
                touchAction: 'none',
            }}
            onClick={onClose}
        >
            <img
                src={src}
                alt=""
                draggable={false}
                style={{
                    width: '100%',
                    height: '100%',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    objectPosition: 'center',
                    borderRadius: '8px',
                    cursor: 'default',
                    pointerEvents: 'none',
                    display: 'block',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                }}
            />
            <button
                type="button"
                aria-label="Close"
                style={{
                    position: 'fixed',
                    top: '12px',
                    right: '12px',
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    background: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '22px',
                    fontWeight: 900,
                    color: '#1e293b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    zIndex: 2147483647,
                }}
                onClick={(e) => { e.stopPropagation(); onClose?.(); }}
            >
                ✕
            </button>
        </div>,
        document.body,
    );
}

/** Event-delegation click-to-expand for images inside a container. */
export function setupExpandableImages(container, openImage) {
    if (!container || typeof openImage !== 'function') return () => {};

    const onClick = (e) => {
        const img = e.target?.closest?.('img');
        if (!img || !container.contains(img)) return;
        if (img.classList.contains('embedded-image--failed')) return;
        if (img.dataset.expandableImage === 'off') return;
        e.preventDefault();
        e.stopPropagation();
        openImage(img.currentSrc || img.src);
    };

    const markImages = () => {
        container.querySelectorAll('img').forEach((img) => {
            if (img.classList.contains('embedded-image--failed')) return;
            if (img.dataset.expandableImage === 'off') return;
            img.style.cursor = 'zoom-in';
        });
    };

    container.addEventListener('click', onClick);
    markImages();

    const mo = new MutationObserver(markImages);
    mo.observe(container, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'class'] });

    return () => {
        container.removeEventListener('click', onClick);
        mo.disconnect();
    };
}

/** Hook: bind expand-on-click for all images under containerRef. */
export function useExpandableImages(containerRef, deps = []) {
    const { expandedSrc, openImage, closeImage } = useImageLightbox();

    useEffect(() => {
        const el = containerRef?.current;
        if (!el) return undefined;
        return setupExpandableImages(el, openImage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openImage, containerRef, ...deps]);

    return { expandedSrc, openImage, closeImage };
}

/** Wrapper for arbitrary HTML/React children with image lightbox. */
export function ExpandableImageHost({
    children,
    className,
    style,
    as: Tag = 'div',
    ...props
}) {
    const ref = useRef(null);
    const { expandedSrc, openImage, closeImage } = useImageLightbox();

    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        return setupExpandableImages(el, openImage);
    }, [openImage, children]);

    return (
        <>
            <Tag ref={ref} className={className} style={style} {...props}>
                {children}
            </Tag>
            <ImageLightboxPortal src={expandedSrc} onClose={closeImage} />
        </>
    );
}
